/**
 * A WeaveIn bot post (metadata.system_event, TRUS-684/685) as a compact card:
 * an icon for the event family (agents / code / deploys / signatures / tasks /
 * workflows / member tasks / automations), the title, an optional body and an
 * "Open" button.
 *
 * "Open" routes internal links (/agents?job=…, /workspace-messaging?channel=…)
 * inside the shell with no page load — a reload would drop a call in
 * progress — and opens external links (GitHub, Render, Vercel) in a new tab.
 */

import React from 'react';
import {
  Bot,
  ClipboardCheck,
  ExternalLink,
  FileSignature,
  GitPullRequest,
  ListChecks,
  Rocket,
  Wand2,
  Workflow,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { config } from '@/src/config';
import { parsePushTarget, routeStaffPushTarget } from '@/lib/pushNavigation';
import { familyOf, type SystemEventFamily, type SystemEventMeta } from '@/services/systemEventsApi';
import { GROUP_LABEL_KEYS } from './eventCatalog';

export const FAMILY_ICON: Record<SystemEventFamily, LucideIcon> = {
  agent: Bot,
  git: GitPullRequest,
  deploy: Rocket,
  signatures: FileSignature,
  tasks: ListChecks,
  workflows: Workflow,
  member_tasks: ClipboardCheck,
  automation: Wand2,
  other: Zap,
};

/** The sender name every bot post shows, whatever the server sends. */
export const SYSTEM_EVENT_SENDER_NAME = 'WeaveIn';

/** Where a link should go: in the app, or a new browser tab. Null when unusable. */
export function classifySystemEventLink(
  link: string | null | undefined,
  origin: string = typeof window !== 'undefined' ? window.location.origin : 'http://localhost',
): { kind: 'internal'; url: string } | { kind: 'external'; url: string } | null {
  if (!link || typeof link !== 'string') return null;
  const trimmed = link.trim();
  if (!trimmed) return null;
  let u: URL;
  try {
    u = new URL(trimmed, origin);
  } catch {
    return null;
  }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
  if (u.origin === origin) return { kind: 'internal', url: `${u.pathname}${u.search}${u.hash}` };
  return { kind: 'external', url: u.toString() };
}

/** Switch the shell to a tab (App.tsx listens for `changeTab`). */
function navigateTab(tab: string) {
  window.dispatchEvent(new CustomEvent('changeTab', { detail: tab }));
}

/** Route an internal link in place. True when the shell handled it. */
export function routeInternalLink(url: string): boolean {
  const target = parsePushTarget(url, window.location.origin);
  if (!target) return false;
  return routeStaffPushTarget(target, { navigateTab, isTriveny: config.isTriveny });
}

interface SystemEventCardProps {
  meta: SystemEventMeta;
  /** Blue bubble: light text. Bot posts sit on the left, so normally 'default'. */
  tone?: 'default' | 'inverted';
}

export function SystemEventCard({ meta, tone = 'default' }: SystemEventCardProps) {
  const { t } = useAppTranslation();
  const family = meta.family || familyOf(meta.event_type);
  const Icon = FAMILY_ICON[family];
  const link = classifySystemEventLink(meta.link);
  const inverted = tone === 'inverted';
  const familyLabel =
    family === 'agent'
      ? t('messaging.systemEvents.family.agent', 'Agents')
      : family === 'git'
        ? t('messaging.systemEvents.family.git', 'Code')
        : family === 'deploy'
          ? t('messaging.systemEvents.family.deploy', 'Deploys')
          : family === 'other'
            ? t('messaging.systemEvents.family.other', 'Event')
            : t(GROUP_LABEL_KEYS[family][0], GROUP_LABEL_KEYS[family][1]);
  const accessibleName = t('messaging.systemEvents.cardLabel', '{{family}} event: {{title}}', {
    family: familyLabel,
    title: meta.title,
  });

  const openInternal = (url: string) => {
    if (routeInternalLink(url)) return;
    // Not a page the shell knows: a normal navigation is the only way there.
    window.location.assign(url);
  };

  return (
    <div
      role="article"
      aria-label={accessibleName}
      className="flex max-w-xl items-start gap-3 py-1"
      data-testid="system-event-card"
      data-family={family}
    >
      <div
        className={cn(
          'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full',
          inverted ? 'bg-white/20 text-white' : 'bg-primary/10 text-primary',
        )}
        aria-hidden="true"
      >
        <Icon className="h-4 w-4" data-testid={`system-event-icon-${family}`} />
      </div>
      <div className="min-w-0 flex-1">
        <div className={cn('break-words text-sm font-semibold', inverted && 'text-white')} data-testid="system-event-title">
          {meta.title}
        </div>
        {meta.body && (
          <div
            className={cn(
              'mt-0.5 whitespace-pre-wrap break-words text-sm',
              inverted ? 'text-white/80' : 'text-muted-foreground',
            )}
          >
            {meta.body}
          </div>
        )}
        {link && (
          <div className="mt-2">
            {link.kind === 'external' ? (
              <a
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-7 items-center gap-1 rounded-lg border-2 border-[var(--primary,#2563EB)] bg-transparent px-3 text-xs font-medium text-[var(--primary,#2563EB)] hover:bg-[var(--primary,#2563EB)]/10 focus:outline-none focus:ring-2 focus:ring-[var(--primary,#2563EB)] focus:ring-offset-2"
                aria-label={t('messaging.systemEvents.openExternal', 'Open {{title}} in a new tab', { title: meta.title })}
                data-testid="system-event-open"
              >
                {t('messaging.systemEvents.open', 'Open')}
                <ExternalLink className="h-3 w-3" aria-hidden="true" />
              </a>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => openInternal(link.url)}
                aria-label={t('messaging.systemEvents.openInternal', 'Open {{title}}', { title: meta.title })}
                data-testid="system-event-open"
              >
                {t('messaging.systemEvents.open', 'Open')}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** "WeaveIn" + a small "Bot" badge, for the sender line of a bot post. */
export function SystemEventSender({ className }: { className?: string }) {
  const { t } = useAppTranslation();
  return (
    <span className={cn('inline-flex items-center gap-1.5', className)} data-testid="system-event-sender">
      <span className="text-sm font-semibold">{SYSTEM_EVENT_SENDER_NAME}</span>
      <span className="inline-flex items-center gap-0.5 rounded border border-primary/30 bg-primary/10 px-1.5 py-0 text-[10px] font-medium text-primary">
        <Bot className="h-3 w-3" aria-hidden="true" />
        {t('messaging.systemEvents.botBadge', 'Bot')}
      </span>
    </span>
  );
}

export default SystemEventCard;
